import React, { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";

/**
 * AnimatedCounter parses numeric values with prefixes and suffixes
 * (e.g. "+200%", "+120%", "60%", "8+ Yrs", "90 Days")
 * and smoothly counts up from 0 to the target number once visible in viewport.
 */
export default function AnimatedCounter({ value, duration = 3.2, delay = 0.25, className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "50px" });
  const [displayNumber, setDisplayNumber] = useState(0);

  // Parse prefix, number, and suffix
  // e.g. "+200%" => prefix: "+", num: 200, suffix: "%"
  // "8+ Yrs" => prefix: "", num: 8, suffix: "+ Yrs"
  const parsed = React.useMemo(() => {
    if (typeof value !== "string" && typeof value !== "number") {
      return null;
    }
    const str = String(value).trim();
    // Regex matching: (optional non-digits)(one or more digits or decimal)(everything else)
    const match = str.match(/^([^0-9.]*)(\d+(?:\.\d+)?)(.*)$/);
    if (!match) {
      return null;
    }
    return {
      prefix: match[1] || "",
      target: parseFloat(match[2]),
      isDecimal: match[2].includes("."),
      suffix: match[3] || ""
    };
  }, [value]);

  useEffect(() => {
    if (!isInView || !parsed) return;

    let controls;
    const timer = setTimeout(() => {
      controls = animate(0, parsed.target, {
        duration,
        // Smooth ease that allows numbers to be clearly seen counting up
        ease: [0.12, 0.65, 0.4, 1],
        onUpdate: (latest) => {
          setDisplayNumber(parsed.isDecimal ? latest.toFixed(1) : Math.round(latest));
        }
      });
    }, delay * 1000);

    return () => {
      clearTimeout(timer);
      if (controls) controls.stop();
    };
  }, [isInView, parsed, duration, delay]);

  if (!parsed) {
    return <span className={className}>{value}</span>;
  }

  return (
    <span ref={ref} className={className}>
      {isInView ? (
        <>
          {parsed.prefix}
          {displayNumber}
          {parsed.suffix}
        </>
      ) : (
        <>
          {parsed.prefix}
          0
          {parsed.suffix}
        </>
      )}
    </span>
  );
}
